import { useEffect } from "react";
import { type ResolvedKeybindingsConfig, type ThreadId } from "@cmmd-forge/contracts";
import { useCommandPaletteStore } from "../../commandPaletteStore";
import { resolveShortcutCommand } from "../../keybindings";
import { isTerminalFocused } from "../../lib/terminalFocus";
import { projectScriptIdFromCommand } from "../../projectScripts";
import { type Project, type ProjectScript } from "../../types";

export function useChatKeyboardShortcuts(input: {
  readonly activeThreadId: ThreadId | null;
  readonly activeProject: Project | null;
  readonly terminalOpen: boolean;
  readonly activeTerminalId: string;
  readonly keybindings: ResolvedKeybindingsConfig;
  readonly canvasOpen: boolean;
  readonly setTerminalOpen: (open: boolean) => void;
  readonly toggleTerminalVisibility: () => void;
  readonly splitTerminal: () => void;
  readonly createNewTerminal: () => void;
  readonly closeTerminal: (terminalId: string) => void;
  readonly runProjectScript: (script: ProjectScript) => Promise<void>;
  readonly onToggleDiff: () => void;
  readonly onToggleCanvas: (open: boolean) => void;
  readonly onTogglePreview: () => void;
  readonly togglePlanSidebar: () => void;
}): void {
  useEffect(() => {
    const handler = (event: globalThis.KeyboardEvent) => {
      const focusedSurface = document.activeElement?.closest<HTMLElement>(
        "[data-chat-shortcut-surface]",
      );
      if (
        focusedSurface != null &&
        focusedSurface.dataset.chatShortcutSurface !== input.activeThreadId
      )
        return;
      if (
        !input.activeThreadId ||
        useCommandPaletteStore.getState().open ||
        event.defaultPrevented
      ) {
        return;
      }
      const command = resolveShortcutCommand(event, input.keybindings, {
        context: {
          terminalFocus: isTerminalFocused(),
          terminalOpen: input.terminalOpen,
        },
      });
      if (
        !command &&
        event.shiftKey &&
        !event.altKey &&
        (event.key.toLowerCase() === "p" || event.code === "KeyP") &&
        (event.metaKey || event.ctrlKey) &&
        !isTerminalFocused()
      ) {
        event.preventDefault();
        event.stopPropagation();
        input.togglePlanSidebar();
        return;
      }
      if (!command) return;

      if (command === "terminal.toggle") {
        event.preventDefault();
        event.stopPropagation();
        input.toggleTerminalVisibility();
        return;
      }

      if (command === "terminal.split") {
        event.preventDefault();
        event.stopPropagation();
        if (!input.terminalOpen) {
          input.setTerminalOpen(true);
        }
        input.splitTerminal();
        return;
      }

      if (command === "terminal.close") {
        event.preventDefault();
        event.stopPropagation();
        if (!input.terminalOpen) return;
        input.closeTerminal(input.activeTerminalId);
        return;
      }

      if (command === "terminal.new") {
        event.preventDefault();
        event.stopPropagation();
        if (!input.terminalOpen) {
          input.setTerminalOpen(true);
        }
        input.createNewTerminal();
        return;
      }

      if (command === "diff.toggle") {
        event.preventDefault();
        event.stopPropagation();
        input.onToggleDiff();
        return;
      }

      if (command === "canvas.toggle") {
        event.preventDefault();
        event.stopPropagation();
        input.onToggleCanvas(!input.canvasOpen);
        return;
      }

      if (command === "preview.toggle") {
        event.preventDefault();
        event.stopPropagation();
        input.onTogglePreview();
        return;
      }

      if (command === "plan.toggle") {
        event.preventDefault();
        event.stopPropagation();
        input.togglePlanSidebar();
        return;
      }

      const scriptId = projectScriptIdFromCommand(command);
      if (!scriptId || !input.activeProject) return;
      const script = input.activeProject.scripts.find((entry) => entry.id === scriptId);
      if (!script) return;
      event.preventDefault();
      event.stopPropagation();
      void input.runProjectScript(script);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [input]);
}
